import React, { useState, useEffect } from 'react';
import {
  Modal,
  Paper,
  Typography,
  Button,
  CircularProgress,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails
} from '@material-ui/core';
import {
  ExpandMore,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import prism from 'prismjs';

import useSnackbar from '../../hooks/useSnackbar';
import Solution from '../../models/Solution';
import utils from '../../libs/utils';

interface SolutionGalleryModalProps {
  specId?: string
  isOpen: boolean
  onClose?(): void
}

const useStyles = makeStyles((theme) => {
  return {
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '95%',
      maxWidth: '650px',
      maxHeight: '95vh'
    },
    header: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing()
    },
    body: {
      flex: 1,
      overflow: 'auto',
      padding: theme.spacing()
    },
    footer: {
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(),
      textAlign: 'right'
    },
    accordionDetails: {
      flexDirection: 'column',
      padding: `0 ${theme.spacing(2)}px ${theme.spacing(2)}px`
    },
    code: {
      width: '100%',
      whiteSpace: 'pre-wrap',
      padding: theme.spacing(),
      backgroundColor: 'rgba(250, 250, 250, 1)',
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`
    }
  };
});

export const SolutionGalleryModal = (props: SolutionGalleryModalProps) => {
  const {
    specId,
    isOpen,
    onClose
  } = props;

  const { renderSnackbar, showMessage: showSnackbarMessage } = useSnackbar();
  const classes = useStyles();

  // solutions
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [isLoadingSolutions, setIsLoadingSolutions] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [deletingSolutions, setDeletingSolutions] = useState<Solution[]>([]);

  const loadSolutions = async () => {
    try {
      setIsLoadingSolutions(true);
      setErrorMessage('');
      setSolutions([]);

      if (specId) {
        const solutions = await Solution.find({ specID: specId });
        setSolutions(solutions);
      }
    }
    catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    }
    finally {
      setIsLoadingSolutions(false);
    }
  };

  const deleteSolution = async (solution: Solution) => {
    try {
      setDeletingSolutions([...deletingSolutions, solution]);

      if (window.confirm(`Are you sure to delete ${solution.name}?`)) {
        await solution.destroy();
        setSolutions(solutions.filter(a => a !== solution));
      }
    }
    catch (e) {
      showSnackbarMessage(`Delete unsuccessful: ${e.message}`, 'error');
    }
    finally {
      setDeletingSolutions(deletingSolutions.filter(a => a !== solution));
    }
  };

  const copySolutionToClipboard = (solution: Solution) => {
    navigator.clipboard.writeText(solution.code);
    showSnackbarMessage('Solution copied to clipboard.');
  };

  useEffect(() => {
    (async() => await loadSolutions())();
  }, [specId]);

  return (
    <>
      { renderSnackbar() }

      <Modal open={isOpen} onClose={onClose} className={classes.modal}>
        <Paper className={classes.wrapper}>
          <div className={classes.header}>
            <div className="d-flex align-items-center">
              <Typography variant="h6">{specId}</Typography>
              <p className="my-0 mx-2">&bull;</p>
              <p className="m-0">Solution Gallery</p>
            </div>
          </div>

          <div className={classes.body}>
            {(() => {
              if (isLoadingSolutions)
                return (
                  <div className="text-center">
                    <CircularProgress size={30} />
                  </div>
                );

              if (errorMessage)
                return (
                  <div className="text-center">
                    <p className="mb-2">
                      <b>Error Loading Items</b> <br/>
                      <span>{errorMessage}</span>
                    </p>
                    <Button
                      className="mr-1"
                      type="button"
                      size="small"
                      onClick={loadSolutions}
                      variant="outlined"
                      color="primary"
                    >
                      Try Again
                    </Button>

                  </div>
                );

              if (!solutions.length)
                return (
                  <p className="text-center">There are no saved solutions yet.</p>
                );

              return solutions.map(solution => (
                <ExpansionPanel>
                  <ExpansionPanelSummary expandIcon={<ExpandMore />}>
                    <Typography>{solution.name}</Typography>
                  </ExpansionPanelSummary>

                  <ExpansionPanelDetails className={classes.accordionDetails}>
                    <div className="d-flex w-100">
                      <code
                        className={classes.code}
                        dangerouslySetInnerHTML={{
                          __html: prism.highlight(
                            solution.code,
                            prism.languages.javascript,
                            'javascript',
                          )
                        }} />
                    </div>

                    <div className="w-100 text-right pt-2">
                      <Button
                        className="mr-1"
                        type="button"
                        size="small"
                        onClick={() => copySolutionToClipboard(solution)}
                        variant="outlined"
                        color="primary"
                      >
                        Copy
                      </Button>

                      <Button
                        className="mr-1"
                        type="button"
                        size="small"
                        onClick={() => deleteSolution(solution)}
                        variant="outlined"
                        color="secondary"
                        startIcon={deletingSolutions.indexOf(solution) >= 0 ? (<CircularProgress size={18} color="secondary" />) : undefined}
                        disabled={deletingSolutions.indexOf(solution) >= 0}
                      >
                        Delete
                      </Button>
                    </div>
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              ));
            })()}
          </div>

          <div className={classes.footer}>
            <Button
              className="mr-1"
              type="button"
              size="small"
              onClick={onClose}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          </div>
        </Paper>
      </Modal>
    </>
  );
};
